/** Layer-structure and layer-property intents. */
import type {
  LayerCreateIntent,
  LayerDeleteIntent,
  LayerGroupIntent,
  LayerId,
  LayerReorderIntent,
  LayerTransformIntent,
  LayerTranslateIntent,
  LayerUngroupIntent,
  LayerUpdateIntent,
} from '@maipo/agent-protocol';
import { LIMITS } from '@maipo/agent-protocol';
import {
  AddLayerCommand,
  GroupLayersCommand,
  RemoveLayerCommand,
  ReorderLayerCommand,
  SetLayerTransformCommand,
  UngroupLayerCommand,
  UpdateLayerCommand,
  type LayerPatch,
} from '../../core/commands';
import { invertMatrix, multiplyMatrix, type Matrix as NativeMatrix } from '../../core/geometry';
import { createAdjustmentLayer, createGroupLayer, findLayer, findLayerParent, layerParentTransform, validateDimensions, type EditorDocument, type Layer } from '../../core/model';
import { isSmartObject } from '../../core/smart-objects';
import { layerStyle } from '../../core/layer-styles';
import { matrixToPublicTransform, publicTransformMatrix } from '../../public-format/adapter';
import { createShapeLayer, createTextLayer } from '../../vector/model';
import { defineIntent, IntentRejection, type Diagnostic } from '../registry';
import {
  ancestorIds,
  checkContainerUnlocked,
  checkUnlocked,
  error,
  layerFor,
  parentKey,
  placementReads,
  requireLayer,
  resolvePlacement,
  resolveRef,
  staticIds,
  toSolidPaint,
  toStroke,
  validatePlacement,
} from './common';
import { mergedAdjustment, validateAdjustment } from './properties';
import { buildRasterLayer, compileCreatedFromBytes, prepareImageLayer, prepareRasterFromAsset, releasePreparedCreate, type PreparedCreate } from './media';

function assertTransformInRange(matrix: readonly number[], layerId: LayerId): void {
  if (!matrix.every((value) => Number.isFinite(value) && Math.abs(value) <= LIMITS.maxTransformComponent)) {
    throw new IntentRejection('LIMIT_EXCEEDED', `The resulting transform of ${layerId} exceeds ±${LIMITS.maxTransformComponent} per component.`, layerId);
  }
  if (!invertMatrix(matrix as unknown as NativeMatrix)) throw new IntentRejection('COMMAND_REJECTED', `The resulting transform of ${layerId} is not invertible.`, layerId);
}

const LAYER_UPDATE_FIELDS: Record<keyof LayerUpdateIntent['set'], keyof LayerPatch> = {
  name: 'name', visible: 'visible', opacity: 'opacity', fillOpacity: 'fillOpacity', blendMode: 'blendMode', labelColor: 'color',
};

export const layerUpdate = defineIntent<LayerUpdateIntent>({
  type: 'layer.update',
  version: 1,
  footprint: (_document, intent, aliases) => {
    const ids = staticIds([intent.layer], aliases);
    return { reads: ids, writes: ids, structural: false, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (layer) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    const patch: LayerPatch = {};
    for (const [key, field] of Object.entries(LAYER_UPDATE_FIELDS) as [keyof LayerUpdateIntent['set'], keyof LayerPatch][]) {
      const value = intent.set[key];
      if (value !== undefined) Object.assign(patch, { [field]: value });
    }
    return [new UpdateLayerCommand(layer.id, patch, 'Update layer')];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    return (Object.entries(intent.set) as [keyof LayerUpdateIntent['set'], unknown][]).map(([key, after]) => ({
      layer: id ?? intent.layer, path: key, before: layer ? layer[LAYER_UPDATE_FIELDS[key]] : null, after,
    }));
  },
});

/** Top-level members of `ids`: drops layers whose ancestor is also listed. */
function outermost(document: EditorDocument, ids: readonly LayerId[]): LayerId[] {
  const set = new Set(ids);
  return [...set].filter((id) => !ancestorIds(document, id).some((ancestor) => set.has(ancestor)));
}

export const layerTranslate = defineIntent<LayerTranslateIntent>({
  type: 'layer.translate',
  version: 1,
  footprint: (document, intent, aliases) => {
    const ids = staticIds(intent.layers, aliases);
    const reads = intent.space === 'parent' ? ids : [...ids, ...ids.flatMap((id) => ancestorIds(document, id))];
    return { reads: [...new Set(reads)], writes: ids, structural: false, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    for (const ref of intent.layers) {
      const layer = layerFor(document, ref, context, diagnostics);
      if (!layer) continue;
      if (layer.kind === 'adjustment') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Adjustment layer ${layer.id} has no position.`, layer.id));
      checkUnlocked(document, layer, 'position', diagnostics, context.policy);
    }
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layers = intent.layers.map((ref) => requireLayer(document, ref, aliases));
    const translate: NativeMatrix = [1, 0, 0, 1, intent.dx, intent.dy];
    return outermost(document, layers.map((layer) => layer.id)).map((id) => {
      const layer = findLayer(document.root, id) as Layer;
      let next: NativeMatrix;
      if (intent.space === 'parent') {
        next = multiplyMatrix(translate, layer.transform);
      } else {
        const parent = layerParentTransform(document.root, id);
        const inverse = parent ? invertMatrix(parent) : null;
        if (!parent || !inverse) throw new IntentRejection('COMMAND_REJECTED', `The parent transform of ${id} is not invertible.`, id);
        next = multiplyMatrix(inverse, multiplyMatrix(translate, multiplyMatrix(parent, layer.transform)));
      }
      assertTransformInRange(next, id);
      return new SetLayerTransformCommand(id, next, 'Move layer');
    });
  },
  describe: (document, intent, aliases) => staticIds(intent.layers, aliases).map((id) => {
    const layer = findLayer(document.root, id);
    return { layer: id, path: 'transform.matrix', before: layer ? [...layer.transform] : null, after: { translateBy: [intent.dx, intent.dy], space: intent.space ?? 'document' } };
  }),
});

export const layerTransform = defineIntent<LayerTransformIntent>({
  type: 'layer.transform',
  version: 1,
  footprint: (_document, intent, aliases) => {
    const ids = staticIds([intent.layer], aliases);
    return { reads: ids, writes: ids, structural: false, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind === 'adjustment') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Adjustment layer ${layer.id} has no transform.`, layer.id));
    checkUnlocked(document, layer, 'position', diagnostics, context.policy);
    if ('matrix' in intent && !invertMatrix(intent.matrix as NativeMatrix)) diagnostics.push(error('COMMAND_REJECTED', 'The matrix is not invertible.', layer.id));
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    let next: NativeMatrix;
    if ('matrix' in intent) {
      next = [...intent.matrix] as unknown as NativeMatrix;
    } else {
      let current;
      try { current = matrixToPublicTransform(layer.transform); } catch {
        throw new IntentRejection('COMMAND_REJECTED', `The transform of ${layer.id} cannot be decomposed; set a matrix instead.`, layer.id);
      }
      next = publicTransformMatrix({ ...current, ...intent.decomposed });
    }
    assertTransformInRange(next, layer.id);
    return [new SetLayerTransformCommand(layer.id, next)];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    return [{ layer: id ?? intent.layer, path: 'matrix' in intent ? 'transform.matrix' : 'transform.decomposed', before: layer ? [...layer.transform] : null, after: 'matrix' in intent ? [...intent.matrix] : intent.decomposed }];
  },
});

export const layerReorder = defineIntent<LayerReorderIntent>({
  type: 'layer.reorder',
  version: 1,
  footprint: (document, intent, aliases) => {
    const ids = staticIds([intent.layer], aliases);
    return { reads: [...ids, ...placementReads(document, intent.to, aliases, ids[0])], writes: ids, structural: true, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (layer) {
      checkUnlocked(document, layer, 'any', diagnostics, context.policy);
      const location = findLayerParent(document.root, layer.id);
      if (location) checkContainerUnlocked(document, location.parent, diagnostics, layer.id, context.policy);
    }
    validatePlacement(document, intent.to, context, diagnostics, layer ?? undefined);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    const target = resolvePlacement(document, intent.to, aliases, layer);
    return [new ReorderLayerCommand(layer.id, target.parentId, target.index)];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    return [{ layer: id ?? intent.layer, path: 'parentId', before: id ? parentKey(document, id) : null, after: intent.to }];
  },
});

export const layerGroup = defineIntent<LayerGroupIntent>({
  type: 'layer.group',
  version: 1,
  footprint: (document, intent, aliases) => {
    const ids = staticIds(intent.layers, aliases);
    return { reads: [...ids, ...new Set(ids.filter((id) => findLayer(document.root, id)).map((id) => parentKey(document, id)))], writes: ids, structural: true, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layers = intent.layers.map((ref) => layerFor(document, ref, context, diagnostics)).filter((layer): layer is Layer => Boolean(layer));
    const parents = new Set(layers.map((layer) => findLayerParent(document.root, layer.id)?.parent));
    if (parents.size > 1) diagnostics.push(error('COMMAND_REJECTED', 'Only sibling layers can be grouped.'));
    for (const layer of layers) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    const [parent] = parents;
    if (parent && layers[0]) checkContainerUnlocked(document, parent, diagnostics, layers[0].id, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const ids = [...new Set(intent.layers.map((ref) => requireLayer(document, ref, aliases).id))];
    const first = ids[0];
    if (first === undefined) throw new IntentRejection('SCHEMA_INVALID', 'layer.group needs at least one layer.');
    return [
      new GroupLayersCommand(ids),
      {
        deferred: (current, table) => {
          const group = findLayerParent(current.root, first)?.parent;
          if (!group || group === current.root) throw new IntentRejection('COMMAND_REJECTED', 'The new group could not be located.', first);
          if (intent.as) table.set(intent.as, group.id);
          return intent.name !== undefined && intent.name !== group.name ? [new UpdateLayerCommand(group.id, { name: intent.name }, 'Rename group')] : [];
        },
      },
    ];
  },
  describe: (document, intent, aliases) => [
    { layer: intent.as ?? '(new group)', path: '(created)', before: null, after: { kind: 'group', name: intent.name ?? 'Group' } },
    ...staticIds(intent.layers, aliases).map((id) => ({ layer: id, path: 'parentId', before: findLayer(document.root, id) ? parentKey(document, id) : null, after: intent.as ?? '(new group)' })),
  ],
});

export const layerUngroup = defineIntent<LayerUngroupIntent>({
  type: 'layer.ungroup',
  version: 1,
  footprint: (document, intent, aliases) => {
    const id = resolveRef(intent.group, aliases);
    const group = id ? findLayer(document.root, id) : undefined;
    const children = group?.kind === 'group' ? group.children.map((child) => child.id) : [];
    return { reads: id && group ? [id, parentKey(document, id)] : id ? [id] : [], writes: id ? [id, ...children] : [], structural: true, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const group = layerFor(document, intent.group, context, diagnostics);
    if (!group) return diagnostics;
    if (group.kind !== 'group') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${group.id} is a ${group.kind} layer, not a group.`, group.id)); return diagnostics; }
    if (context.policy.kinds === 'strict' && isSmartObject(group)) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${group.id} is a smart object; rasterize or edit it in the app.`, group.id));
    checkUnlocked(document, group, 'any', diagnostics, context.policy);
    const location = findLayerParent(document.root, group.id);
    if (location) checkContainerUnlocked(document, location.parent, diagnostics, group.id, context.policy);
    const identity = group.transform.every((value, index) => value === [1, 0, 0, 1, 0, 0][index]);
    const style = layerStyle(group);
    if (!identity || group.opacity !== 1 || group.fillOpacity !== 1 || group.blendMode !== 'normal' || group.mask || group.clipping || style.dropShadow || style.stroke) {
      diagnostics.push({ severity: 'warning', code: 'group-properties-discarded', message: 'Ungrouping discards the group\'s own transform, opacity, blend mode, mask and styles, as the native Ungroup command does.', layer: group.id });
    }
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => [new UngroupLayerCommand(requireLayer(document, intent.group, aliases).id)],
  describe: (_document, intent, aliases) => [{ layer: resolveRef(intent.group, aliases) ?? intent.group, path: '(deleted)', before: { kind: 'group' }, after: null }],
});

export const layerDelete = defineIntent<LayerDeleteIntent>({
  type: 'layer.delete',
  version: 1,
  footprint: (document, intent, aliases) => {
    const ids = staticIds(intent.layers, aliases);
    const descendants = ids.flatMap((id) => {
      const layer = findLayer(document.root, id);
      const collect = (candidate: Layer): LayerId[] => candidate.kind === 'group' ? candidate.children.flatMap((child) => [child.id, ...collect(child)]) : [];
      return layer ? collect(layer) : [];
    });
    return { reads: [...new Set(ids.filter((id) => findLayer(document.root, id)).map((id) => parentKey(document, id)))], writes: [...ids, ...descendants], structural: true, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    for (const ref of intent.layers) {
      const layer = layerFor(document, ref, context, diagnostics);
      if (!layer) continue;
      checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    }
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => outermost(document, intent.layers.map((ref) => requireLayer(document, ref, aliases).id)).map((id) => new RemoveLayerCommand(id)),
  describe: (_document, intent, aliases) => staticIds(intent.layers, aliases).map((id) => ({ layer: id, path: '(deleted)', before: { id }, after: null })),
});

function buildLayer(intent: LayerCreateIntent): Layer {
  const spec = intent.spec;
  switch (spec.kind) {
    case 'text': {
      const layer = createTextLayer();
      layer.name = spec.name;
      layer.transform = [1, 0, 0, 1, spec.at.x, spec.at.y];
      layer.text = {
        ...layer.text,
        value: spec.content,
        style: { ...layer.text.style, ...spec.style, ...(spec.style?.color ? { color: spec.style.color.toLowerCase() } : {}) },
        box: { ...layer.text.box, ...spec.box },
      };
      return layer;
    }
    case 'shape': {
      const layer = createShapeLayer(spec.shape);
      layer.name = spec.name;
      layer.transform = [1, 0, 0, 1, spec.rect.x, spec.rect.y];
      const [object] = layer.vector.objects;
      if (!object || object.type === 'path') throw new IntentRejection('COMMAND_REJECTED', 'Shape construction failed.');
      object.name = spec.name;
      const radius = spec.radius ?? 0;
      if (object.type === 'rectangle') object.geometry = { x: 0, y: 0, width: spec.rect.width, height: spec.rect.height, radius: { topLeft: radius, topRight: radius, bottomRight: radius, bottomLeft: radius } };
      else object.geometry = { x: 0, y: 0, width: spec.rect.width, height: spec.rect.height };
      if (spec.fill !== undefined) object.fill = spec.fill ? toSolidPaint(spec.fill) : null;
      object.stroke = spec.stroke ? toStroke(spec.stroke) : null;
      return layer;
    }
    case 'group':
      return createGroupLayer(spec.name);
    case 'adjustment': {
      const layer = createAdjustmentLayer(spec.name);
      if (spec.set) Object.assign(layer.adjustment, mergedAdjustment(layer.adjustment, spec.set));
      return layer;
    }
    case 'raster':
      return buildRasterLayer(spec);
    case 'image':
      throw new IntentRejection('UNSUPPORTED_COMMAND', 'Image layers prepare asynchronously.');
  }
}

/** Kinds built from bytes prepare asynchronously (decode, resource staging); the others compile directly. */
function fromBytes(intent: LayerCreateIntent): boolean {
  return intent.spec.kind === 'image' || (intent.spec.kind === 'raster' && intent.spec.asset !== undefined);
}

export const layerCreate = defineIntent<LayerCreateIntent>({
  type: 'layer.create',
  version: 1,
  footprint: (document, intent, aliases) => ({ reads: placementReads(document, intent.to, aliases), writes: [], structural: true, document: false }),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    if (intent.spec.kind === 'raster' && intent.spec.asset === undefined) {
      if (intent.spec.width === undefined || intent.spec.height === undefined) diagnostics.push(error('SCHEMA_INVALID', 'A raster layer without an asset needs width and height.'));
      else {
        try { validateDimensions(intent.spec.width, intent.spec.height); } catch (cause) { diagnostics.push(error('LIMIT_EXCEEDED', cause instanceof Error ? cause.message : String(cause))); }
      }
    }
    if (intent.spec.kind === 'shape' && intent.spec.shape === 'ellipse' && intent.spec.radius !== undefined) diagnostics.push(error('COMMAND_REJECTED', 'Ellipses have no corner radius.'));
    if (intent.spec.kind === 'adjustment' && intent.spec.set) diagnostics.push(...validateAdjustment(createAdjustmentLayer('probe').adjustment, intent.spec.set));
    validatePlacement(document, intent.to, context, diagnostics);
    return diagnostics;
  },
  prepare: async (session, intent, signal, context) => {
    if (intent.spec.kind === 'image') return prepareImageLayer(session, intent.spec, signal, context);
    if (intent.spec.kind === 'raster') return prepareRasterFromAsset(session, intent.spec, signal, context);
    return undefined;
  },
  needsPrepare: fromBytes,
  releasePrepared: (_intent, prepared) => releasePreparedCreate(prepared),
  compile: (document, intent, prepared, aliases) => {
    if (fromBytes(intent)) return compileCreatedFromBytes(document, intent, prepared as PreparedCreate | undefined, aliases);
    const layer = buildLayer(intent);
    const target = resolvePlacement(document, intent.to, aliases);
    if (intent.as) aliases.set(intent.as, layer.id);
    return [new AddLayerCommand(layer, target.parentId, target.index)];
  },
  describe: (_document, intent) => [{ layer: intent.as ?? '(new layer)', path: '(created)', before: null, after: { kind: intent.spec.kind === 'shape' ? 'vector' : intent.spec.kind, name: intent.spec.name } }],
});

